import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Users,
  GraduationCap,
  BookOpen,
  DollarSign,
  TrendingUp,
  UserCheck,
  Calendar,
  Building2,
  Bus,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  FileText,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Breadcrumb } from "./Breadcrumb";
import { useState } from "react";
import API from "./api/api01";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Server,
  Database,
  Cpu,
  RefreshCcw,
  CheckCircle2 as CheckIcon,
  XCircle as ErrorIcon,
  Loader2,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { PATHS } from "./config/paths";

interface SuperadminDashboardProps {
  user: {
    name: string;
    role: string;
    email: string;
  };
}

export function SuperadminDashboard({ user }: SuperadminDashboardProps) {
  const navigate = useNavigate();
  // Mock data for charts
  const enrollmentData: any[] = [];
  const revenueData: any[] = [];
  const courseDistribution: any[] = [];
  const recentActivities: any[] = [];
  const quickStats: any[] = [
    { title: "Total Students", value: "0", change: "0%", trend: "up", icon: Users, color: "bg-blue-500", bgLight: "bg-blue-50 dark:bg-blue-500/10", textColor: "text-blue-600 dark:text-blue-400" },
    { title: "Total Teachers", value: "0", change: "0%", trend: "up", icon: GraduationCap, color: "bg-emerald-500", bgLight: "bg-emerald-50 dark:bg-emerald-500/10", textColor: "text-emerald-600 dark:text-emerald-400" },
    { title: "Active Courses", value: "0", change: "0", trend: "up", icon: BookOpen, color: "bg-purple-500", bgLight: "bg-purple-50 dark:bg-purple-500/10", textColor: "text-purple-600 dark:text-purple-400" },
    { title: "Revenue (This Month)", value: "₹0", change: "0%", trend: "up", icon: DollarSign, color: "bg-amber-500", bgLight: "bg-amber-50 dark:bg-amber-500/10", textColor: "text-amber-600 dark:text-amber-400" },
  ];

  const [isExporting, setIsExporting] = useState(false);
  const [healthData, setHealthData] = useState<any>(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);

  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      const response = await API.get("/dashboard/export", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "dashboard_report.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSystemHealth = async () => {
    try {
      setShowHealthModal(true);
      setIsHealthLoading(true);
      const response = await API.get("/dashboard/health");
      setHealthData(response.data);
    } catch (error) {
      console.error("Health check error:", error);
      toast.error("Failed to fetch system health");
    } finally {
      setIsHealthLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ animation: 'fadeInDown 0.4s ease both' }}
        >
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Superadmin Dashboard
            </h1>

            <Breadcrumb items={[{ label: "Dashboard" }]} className="mt-2" />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={handleExportReport}
              disabled={isExporting}
            >
              {isExporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              {isExporting ? "Exporting..." : "Export Report"}
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 rounded-lg text-white"
              onClick={handleSystemHealth}
            >
              <Activity className="h-4 w-4 mr-2" />
              System Health
            </Button>
          </div>
        </div>

        {/* Welcome Card */}
        <Card
        className="relative overflow-hidden border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0B0F19] transition-all duration-300"
          style={{ animation: 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both', animationDelay: '80ms' }}
        >
          {/* Theme-aware Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-100" />
          <div className="absolute -right-16 -top-16 w-64 h-64 bg-primary/10 dark:bg-primary/5 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-[60px] pointer-events-none" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight mb-1">
                  Welcome back, {user.name}! 👋
                </h2>

                <p className="text-[13px] font-medium text-muted-foreground">
                  Here's what's happening with your college today.
                </p>

              </div>
              <div className="hidden md:flex items-center gap-2">
                <Badge variant="outline" className="px-3 py-1">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="bg-white dark:bg-[#0B0F19] border-slate-200 dark:border-white/[0.05] relative overflow-hidden group hover:border-primary/20 dark:hover:border-white/[0.1] transition-all duration-300"
                style={{
                  animation: 'scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
                  animationDelay: `${index * 100 + 200}ms`,
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{stat.title}</p>
                      <p className="text-2xl font-semibold tracking-tight">{stat.value}</p>

                      <div className="flex items-center gap-1">
                        {stat.trend === "up" ? (
                          <ArrowUpRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-600" />
                        )}
                        <span
                          className={`text-sm font-medium ${stat.trend === "up" ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {stat.change}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          vs last period
                        </span>
                      </div>
                    </div>
                    <div className={`h-12 w-12 rounded-lg ${stat.bgLight} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${stat.textColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Row */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          style={{ animation: 'fadeInUp 0.45s ease both', animationDelay: '440ms' }}
        >
          {/* Enrollment Trend */}
          <Card className="bg-white dark:bg-[#0B0F19] border-slate-200 dark:border-white/[0.05]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-sm font-semibold uppercase tracking-wider">
                <span>Student Enrollment Trend</span>

                <Badge variant="outline">Last 6 Months</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={enrollmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="students"
                    stroke="#6366F1"
                    strokeWidth={2}
                    dot={{ fill: "#6366F1", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Revenue Trend */}
          <Card className="bg-white dark:bg-[#0B0F19] border-slate-200 dark:border-white/[0.05]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-sm font-semibold uppercase tracking-wider">
                <span>Revenue Analysis</span>

                <Badge variant="outline">Last 6 Months</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#10B981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          style={{ animation: 'fadeInUp 0.45s ease both', animationDelay: '540ms' }}
        >
          {/* Course Distribution */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle>Course Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={courseDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }: any) => {
                      const total = courseDistribution.reduce((sum, item) => sum + item.value, 0);
                      const percent = (value / total) * 100;
                      return `${name} ${percent.toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {courseDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <Card className="lg:col-span-2 bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-sm font-semibold uppercase tracking-wider">
                <span>Recent Activities</span>
                <Button variant="ghost" size="sm" className="text-[11px] font-semibold uppercase tracking-widest">
                  View All
                </Button>
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={activity.avatar} />
                      <AvatarFallback>{activity.user.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-[13px]">
                        <span className="font-semibold">{activity.user}</span>{" "}
                        <span className="text-muted-foreground font-medium">{activity.action}</span>
                      </p>
                      <p className="text-[10px] uppercase font-semibold tracking-widest text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {activity.time}
                      </p>
                    </div>

                    {activity.type === "success" && (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    )}
                    {activity.type === "warning" && (
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    )}
                    {activity.type === "info" && (
                      <Activity className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card
          className="bg-card"
          style={{ animation: 'fadeInUp 0.45s ease both', animationDelay: '620ms' }}
        >
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.ADD_CLIENT)}>
                <Users className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Add Client</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.ADD_CUSTOMER)}>
                <Users className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Add Customer</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.ADD_COURSE)}>
                <BookOpen className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Add Course</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.MANAGE_ATTENDANCE)}>
                <Calendar className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Mark Attendance</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.FEES_STRUCTURE)}>
                <DollarSign className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Fee Collection</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4 hover:border-primary"
                onClick={() => navigate(PATHS.REPORTS)}>
                <FileText className="h-6 w-6 text-primary" />
                <span className="text-[10px] text-center font-semibold uppercase tracking-widest">Generate Report</span>
              </Button>

            </div>
          </CardContent>
        </Card>

        {/* System Health Modal */}
        <Dialog open={showHealthModal} onOpenChange={setShowHealthModal}>
          <DialogContent className="sm:max-w-md bg-white dark:bg-[#141414] border-none shadow-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl font-semibold">
                <Server className="h-6 w-6 text-primary" />
                System Health
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Real-time status of backend services and infrastructure.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 py-4">
              {isHealthLoading ? (
                <div className="flex flex-col items-center justify-center py-10 space-y-4">
                  <div className="relative">
                    <Loader2 className="h-12 w-12 text-primary animate-spin" />
                    <Activity className="h-6 w-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-sm font-medium animate-pulse">Running diagnostics...</p>
                </div>
              ) : healthData ? (
                <div className="space-y-4">
                  <HealthItem
                    icon={Database}
                    label="Primary Database"
                    status={healthData.database}
                    description="PostgreSQL"
                  />
                  <HealthItem
                    icon={Cpu}
                    label="Redis Cache"
                    status={healthData.redis}
                    description="Session & Rate Limiting"
                  />
                  <HealthItem
                    icon={RefreshCcw}
                    label="RabbitMQ"
                    status={healthData.rabbitmq}
                    description="Task Queue & Email Services"
                  />

                  <div className={`mt-6 p-4 rounded-xl border flex items-center justify-between ${healthData.status === "healthy"
                    ? "bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400"
                    : "bg-red-500/10 border-red-500/20 text-red-700 dark:text-red-400"
                    }`}>
                    <div className="flex items-center gap-2">
                      <Pulse status={healthData.status} />
                      <span className="font-semibold uppercase tracking-wider text-sm">
                        System {healthData.status}
                      </span>
                    </div>
                    <span className="text-xs opacity-70">
                      All systems operational
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setShowHealthModal(false)}
                className="bg-accent/10 hover:bg-accent/20 text-foreground border-none rounded-lg px-6"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function HealthItem({ icon: Icon, label, status, description }: any) {
  const isOk = status === "ok";
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-100 dark:border-slate-800/50 transition-all hover:shadow-md">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${isOk ? "bg-green-500/10" : "bg-red-500/10"}`}>
          <Icon className={`h-5 w-5 ${isOk ? "text-green-600" : "text-red-600"}`} />
        </div>
        <div>
          <p className="font-semibold text-[13px] tracking-tight">{label}</p>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{description}</p>
        </div>

      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-semibold uppercase ${isOk ? "text-green-600" : "text-red-600"}`}>
          {isOk ? "Operational" : "Error"}
        </span>
        {isOk ? <CheckIcon className="h-5 w-5 text-green-600" /> : <ErrorIcon className="h-5 w-5 text-red-600" />}
      </div>
    </div>
  );
}

function Pulse({ status }: { status: string }) {
  const isHealthy = status === "healthy";
  return (
    <span className="relative flex h-3 w-3">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isHealthy ? "bg-green-400" : "bg-red-400"}`}></span>
      <span className={`relative inline-flex rounded-full h-3 w-3 ${isHealthy ? "bg-green-500" : "bg-red-500"}`}></span>
    </span>
  );
}
